import {
  type BufferGeometry,
  Color,
  InstancedBufferAttribute,
  InstancedMesh,
  type Material,
  type Matrix4,
  NormalBlending,
  ShaderMaterial,
} from 'three'

/**
 * A pool of instanced **mesh** particles with a per-instance tint and alpha.
 *
 * The ice-break shards (`ice_vfx`, `m_RenderMode: 4` Mesh) carry a
 * `ColorModule` gradient whose alpha runs 0 → 1 → 1 → 0 over a shard's life.
 * `InstancedMesh` has `instanceColor` for the RGB but nothing for the alpha,
 * and folding alpha into the colour is the mistake `ParticlePool` documents —
 * against `NormalBlending` a dark tint is not a faint shard. So alpha travels
 * as its own instanced attribute, exactly as `ParticlePool` does for sprites;
 * this is that idea for a caller-supplied geometry and a flat colour instead
 * of a texture. Not merged with it because the sprite pool owns a plane, a
 * map and a flipbook frame, none of which a mesh shard has.
 *
 * `colour` and `opacity` are the material's own — for the shards `Ice 1.mat`'s
 * `_Color` (`ICE_SHARD_MATERIAL`, 🔍) at 1. The per-instance tint and alpha
 * multiply into them, and the caller decides what to push there
 * (`shardDrawColour`).
 *
 * `opaque` mirrors the material's blend state: `Ice 1.mat` is `_Surface 0`,
 * `_ZWrite 1`, so the pool writes depth and is sorted with the opaques, which
 * is also what keeps a pile of shards on the ground from showing through one
 * another. A translucent pool (`opaque: false`) is the old arrangement, kept
 * for a caller whose material really does blend.
 */
export class ShardPool {
  readonly mesh: InstancedMesh
  private readonly alphas: InstancedBufferAttribute
  private readonly geometry: BufferGeometry
  private readonly material: Material
  private cursor = 0

  /** Whether `material` is the pool's own flat shader (disposed with it) or a caller's. */
  private readonly ownsMaterial: boolean

  constructor(
    source: BufferGeometry,
    colour: Color | number,
    opacity: number,
    capacity: number,
    name: string,
    { opaque = false, material }: { opaque?: boolean; material?: Material } = {},
  ) {
    // The attribute has to live on the geometry, and the geometry is shared
    // with whoever else draws this mesh, so the pool draws a clone.
    this.geometry = source.clone()
    this.alphas = new InstancedBufferAttribute(new Float32Array(capacity), 1)
    this.geometry.setAttribute('aAlpha', this.alphas)

    // A caller's material — the ice shell's own `ToonMaterial` for the shards
    // (`ICE_SHARD_MATERIAL.drawnAs`) — draws the instances lit and textured
    // like the body they came off; `colour`/`opacity` and the per-instance
    // tint and alpha then go unread, since a ToonMaterial has no `aAlpha` and
    // no `instanceColor` term. The pool's own flat shader is the alternative.
    this.ownsMaterial = !material
    this.material =
      material ??
      new ShaderMaterial({
      uniforms: {
        colour: { value: new Color(colour) },
        opacity: { value: opacity },
      },
      transparent: !opaque,
      depthWrite: opaque,
      blending: NormalBlending,
      vertexShader: `
        attribute float aAlpha;
        varying float vAlpha;
        varying vec3 vTint;
        void main() {
          vAlpha = aAlpha;
          #ifdef USE_INSTANCING_COLOR
            vTint = instanceColor;
          #else
            vTint = vec3(1.0);
          #endif
          gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 colour;
        uniform float opacity;
        varying float vAlpha;
        varying vec3 vTint;
        void main() {
          gl_FragColor = vec4(colour * vTint, opacity * vAlpha);
          if (gl_FragColor.a <= 0.001) discard;
        }
      `,
    })

    this.mesh = new InstancedMesh(this.geometry, this.material, capacity)
    this.mesh.name = name
    this.mesh.count = 0
    // Shards live in world space and are placed by matrix; the pool's own
    // bounds mean nothing, and culling on them hides whole bursts.
    this.mesh.frustumCulled = false
    // Force `instanceColor` into existence so `USE_INSTANCING_COLOR` is
    // defined; without a first write three.js never allocates it.
    this.mesh.setColorAt(0, new Color(0xffffff))
  }

  /** Start a frame. */
  begin(): void {
    this.cursor = 0
  }

  /** Place one shard. Returns false once the pool is full. */
  push(matrix: Matrix4, tint: readonly [number, number, number], alpha: number): boolean {
    if (this.cursor >= this.mesh.instanceMatrix.count) return false
    const i = this.cursor++
    this.mesh.setMatrixAt(i, matrix)
    this.mesh.setColorAt(i, _tint.setRGB(tint[0], tint[1], tint[2]))
    this.alphas.setX(i, alpha)
    return true
  }

  /** Finish a frame and upload. */
  end(): void {
    this.mesh.count = this.cursor
    this.mesh.instanceMatrix.needsUpdate = true
    this.alphas.needsUpdate = true
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true
  }

  dispose(): void {
    this.geometry.dispose()
    if (this.ownsMaterial) this.material.dispose()
    this.mesh.removeFromParent()
  }
}

const _tint = new Color()
